import { MatrixClient, UploadResponse } from 'matrix-js-sdk/src/matrix';

import { fetchMediaWithAuth } from 'modules/Authentication/utils';
import logger from 'utils/logging/faro';

export interface UploadMediaOptions {
  /**
   * MIME type of the file
   */
  type?: string;
  /**
   * Progress handler for tracking upload progress
   */
  progressHandler?: (progress: { loaded: number; total: number }) => void;
  /**
   * Whether to include filename in upload
   */
  includeFilename?: boolean;
}

/**
 * Upload media (image, file, etc.) to Matrix media repository
 * This is a generic utility for uploading any type of media to Matrix
 *
 * @param matrixClient - Matrix client instance
 * @param file - File or Blob to upload
 * @param options - Upload options
 * @returns Promise resolving to mxc:// URI
 *
 * @example
 * ```ts
 * const mxcUri = await uploadMediaToMatrix(matrixClient, imageFile, { type: 'image/jpeg' });
 * ```
 */
export async function uploadMediaToMatrix(
  matrixClient: MatrixClient,
  file: File | Blob,
  { type, progressHandler, includeFilename }: UploadMediaOptions = {}
): Promise<string> {
  try {
    // Convert File to ArrayBuffer then to Blob (ensures consistent handling)
    const isFileType = file instanceof File;
    const arrayBuffer = isFileType ? await file.arrayBuffer() : await file.arrayBuffer();
    const blob = new Blob([arrayBuffer], { type: type || (isFileType ? file.type : undefined) });

    // Upload to Matrix
    const response: UploadResponse = await matrixClient.uploadContent(blob, {
      type,
      progressHandler,
      includeFilename,
    });

    if (!response?.content_uri) {
      throw new Error('Upload failed: No content URI returned');
    }

    return response.content_uri; // This is the mxc:// URI
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to upload media to Matrix: ${error.message}`);
    }
    throw new Error('Failed to upload media to Matrix: Unknown error');
  }
}

export interface FetchMediaOptions {
  /**
   * Width for thumbnail/resize
   */
  width?: number;
  /**
   * Height for thumbnail/resize
   */
  height?: number;
  /**
   * Resize method: 'crop' | 'scale'
   */
  method?: 'crop' | 'scale';
}

/**
 * Fetch media from Matrix and create a blob URL for display
 * This avoids ORB (Opaque Response Blocking) issues by fetching with authentication
 *
 * @param matrixClient - Matrix client instance
 * @param mxcUri - mxc:// URI
 * @param options - Fetch options (width, height, method)
 * @returns Blob URL or null if fetch fails
 *
 * @example
 * ```ts
 * const blobUrl = await fetchMediaAsBlobUrl(matrixClient, 'mxc://...', { width: 100, height: 100, method: 'crop' });
 * ```
 */
export async function fetchMediaAsBlobUrl(
  matrixClient: MatrixClient | null,
  mxcUri: string,
  options?: FetchMediaOptions
): Promise<string | null> {
  if (!matrixClient || !mxcUri) return null;

  try {
    // Convert mxc:// to HTTP URL with authentication enabled
    const httpUrl = matrixClient.mxcUrlToHttp(
      mxcUri,
      options?.width,
      options?.height,
      options?.method || 'scale',
      undefined,
      undefined,
      true // allowDirectLinks (enables authentication)
    );

    if (!httpUrl) return null;

    // Fetch with authentication to avoid ORB issues
    const accessToken = matrixClient.getAccessToken();
    if (!accessToken) {
      logger.warn('Cannot fetch media: missing access token');
      return null;
    }

    const response = await fetchMediaWithAuth(httpUrl, accessToken);

    if (!response.ok) {
      logger.warn(`Failed to fetch media: ${response.status} ${response.statusText}`);
      return null;
    }

    // Convert response to blob and create blob URL
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    logger.warn('Error fetching media from Matrix', error);
    return null;
  }
}
